<template>
  <span
    class="relative inline-flex shrink-0 items-center justify-center rounded-xl"
    :class="chipSize"
    :style="chipStyle"
  >
    <!-- A hairline of light along the top edge. This is the whole trick: a
         flat tinted square reads as a sticker, the same square with a lit top
         edge and a shadow under it reads as a raised chip. -->
    <span
      class="pointer-events-none absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-white/[0.08]"
      aria-hidden="true"
    ></span>

    <img
      v-if="src && !broken"
      :src="src"
      :alt="alt"
      class="relative object-contain"
      :class="logoSize"
      :style="logoShadow"
      loading="lazy"
      @error="broken = true"
    />
    <span v-else class="relative font-bold" :class="fallbackSize" :style="{ color: accent }">
      {{ abbr }}
    </span>
  </span>
</template>

<script>
import { teamAccent, teamTint } from "../utils/teamColors.js";
import { teamLogo } from "../utils/teamLogo.js";

// Chip box, logo inside it, and the abbreviation fallback, per size. The logo
// is deliberately ~78% of the chip: the old 70% left a moat of tint around
// every mark and made the logos read as small.
const SIZES = {
  xs: { chip: "h-7 w-7", logo: "h-[22px] w-[22px]", text: "text-[9px]" },
  sm: { chip: "h-9 w-9", logo: "h-7 w-7", text: "text-[10px]" },
  md: { chip: "h-11 w-11", logo: "h-[34px] w-[34px]", text: "text-xs" },
};

export default {
  name: "TeamLogoChip",
  props: {
    // Full display name where we have it -- the colour table is keyed on it --
    // falling back to the abbreviation, which is indexed too.
    team: { type: String, default: "" },
    abbr: { type: String, default: "" },
    // An explicit logo URL from the API, if there is one. /api/v1/games sends
    // none, so most chips derive theirs from the abbreviation.
    logo: { type: String, default: "" },
    size: { type: String, default: "md" },
  },
  data() {
    return { broken: false };
  },
  computed: {
    src() {
      return this.logo || teamLogo(this.abbr);
    },
    alt() {
      return this.team || this.abbr;
    },
    key() {
      return this.team || this.abbr;
    },
    accent() {
      return teamAccent(this.key);
    },
    chipSize() {
      return (SIZES[this.size] || SIZES.md).chip;
    },
    logoSize() {
      return (SIZES[this.size] || SIZES.md).logo;
    },
    fallbackSize() {
      return (SIZES[this.size] || SIZES.md).text;
    },
    // Light falls from the top-left, so the tint is strongest there and the
    // chip sinks into near-black at the bottom-right. That gradient is what
    // keeps a white-heavy mark (Seahawks) and a dark one (Raiders) both
    // legible on the same row -- neither sits on a flat field of its own hue.
    chipStyle() {
      const k = this.key;
      return {
        backgroundImage: `linear-gradient(145deg, ${teamTint(k, 0.32)}, ${teamTint(
          k,
          0.1
        )} 58%, rgba(2, 8, 20, 0.55))`,
        border: `1px solid ${teamTint(k, 0.42)}`,
        boxShadow: `inset 0 1px 0 0 ${teamTint(k, 0.35)}, 0 1px 3px rgba(0, 0, 0, 0.45)`,
      };
    },
    logoShadow() {
      return { filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.55))" };
    },
  },
  watch: {
    // A recycled chip pointed at a different team deserves a fresh attempt.
    src() {
      this.broken = false;
    },
  },
};
</script>
